import { createColumnHelper } from "@tanstack/react-table";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { truncateString } from "../../../Utils/StringHelper";
import { TanStackTable } from "../TanStackTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faArrowRight, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useBoundStore } from "../../../Store/store";
import dayjs from "dayjs";

export const InvoicesTable = (props) => {
    const {
        userId,
        branchId,
        invoices: propInvoices,
        forceNewData = false,
        showActions = false,
        onMarkAsPaid = null
    } = props;

    const [invoices, setInvoices] = useState(propInvoices || []);
    const [metaData, setMetaData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(!!(userId || branchId));
    const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

    const getInvoicesForUser = useBoundStore((state) => state.getInvoicesForUser);
    const {t} = useTranslation();

    useEffect(() => {
        if (propInvoices) {
            setInvoices(propInvoices);
        }
    }, [propInvoices]);

    useEffect(() => {
        const fetchInvoices = async () => {
            setLoading(true);
            const result = await getInvoicesForUser(userId, currentPage, branchId);
            setInvoices(result.data);
            setMetaData(result.meta);
            setLoading(false);
        };

        if (userId || branchId) {
            fetchInvoices();
        }
    }, [userId, branchId, currentPage, forceNewData, getInvoicesForUser]);

    const handleMarkAsPaid = async (invoiceId) => {
        if (onMarkAsPaid) {
            setLoadingInvoiceId(invoiceId);
            await onMarkAsPaid(invoiceId);
            if (userId || branchId) {
                const result = await getInvoicesForUser(userId, currentPage, branchId);
                setInvoices(result.data);
                setMetaData(result.meta);
            }
            setLoadingInvoiceId(null);
        }
    };

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('id', {
            header: t('tables.invoice_number'),
            cell: props => truncateString(props.cell.getValue()),
        }),
        columnHelper.accessor('performance_statement', {
            header: t('tables.invoice_period'),
            cell: props => {
                return dayjs.unix(props.cell.getValue()?.from?.timestamp).format('DD/MM/YYYY')
            },
        }),
        columnHelper.accessor('freelancer', {
            header: i18next.t('shifts_module.column_freelancer'),
            cell: props => truncateString(`${props.cell.getValue()?.firstname ?? '-'} ${props.cell.getValue()?.lastname ?? ''}`),
        }),
        columnHelper.accessor('branch', {
            header: t('tables.branch'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('amount', {
            header: t('tables.amount'),
            cell: props => `€ ${truncateString(props.cell.getValue())}`,
        }),
        columnHelper.accessor('document', {
            header: t('tables.document'),
            cell: props => {
                if ((props.cell.getValue() !== null && props.cell.getValue() !== undefined) && props.cell.getValue().hasOwnProperty('url')) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')} />
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            cell: props => {
                const isPaid = props.row.original.status === 'paid';
                const isLoading = loadingInvoiceId === props.row.original.id;

                return (
                    <div className={'flex flex-row space-x-2 items-center'}>
                        <div>
                            {['pending', 'open'].includes(props.row.original.status) ? <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('general.status.open')}</div> : ''}
                            {isPaid ? <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.paid')}</div> : ''}
                        </div>
                        {!isPaid && onMarkAsPaid && (
                            <button
                                onClick={() => handleMarkAsPaid(props.row.original.id)}
                                disabled={isLoading}
                                className={`flex items-center justify-center bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title={t('invoices.mark_as_paid')}
                            >
                                {isLoading ? (
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                        {t('general.status.paid')}
                                    </>
                                )}
                            </button>
                        )}
                        {showActions && (
                            <Link
                                to={`/admin/invoices/${props.row.original.id}`}
                                className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                            >
                                <FontAwesomeIcon icon={faArrowRight} className={'text-sm'} />
                            </Link>
                        )}
                    </div>
                )
            },
        })
    ]

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (invoices.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                {t('invoices.no_results')}
            </div>
        );
    }

    return (
        <div>
            <TanStackTable
                columns={columns}
                tableData={invoices}
                forceNewData={forceNewData}
                laravelPaginationMetaData={metaData}
                onChangePage={(page) => setCurrentPage(page + 1)}
            />
        </div>
    )
}